'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { type BestQuote, parseBestQuoteFrame } from '@/lib/api/futures/client';
import { WS_ENDPOINTS } from '@/lib/config/websocket';
import { useWebSocket } from '@/lib/hooks/useWebSocket';
import type { AnyWebSocketMessage } from '@/lib/types/websocket';

/** A book ticks many times a second: the quotes re-render their reader once per flush. */
const FLUSH_MS = 250;

/**
 * Streams `/ws/book/best` for `pairIds` (pass a memoized array) while mounted: the
 * close-side price the terminal's live uPnL uses. Mirrors trade's `useBestQuoteSocket`.
 */
export function useFuturesBestQuoteWebSocket(pairIds: number[]) {
  const [quotes, setQuotes] = useState<ReadonlyMap<number, BestQuote>>(new Map());
  const subscribedRef = useRef(new Set<number>());
  const pendingRef = useRef(new Map<number, BestQuote>());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    flushTimerRef.current = null;
    const updates = pendingRef.current;
    pendingRef.current = new Map();
    setQuotes((prev) => new Map([...prev, ...updates]));
  }, []);

  const handleMessage = useCallback(
    (msg: AnyWebSocketMessage) => {
      const quote = parseBestQuoteFrame(msg);
      if (!quote) return;
      pendingRef.current.set(quote.pairId, quote);
      flushTimerRef.current ??= setTimeout(flush, FLUSH_MS);
    },
    [flush],
  );

  const { send, readyState } = useWebSocket({
    url: pairIds.length > 0 ? WS_ENDPOINTS.futuresBookBest : undefined,
    onMessage: handleMessage,
  });

  // A closed socket forgets its subscriptions; an open one gets the added pairs and drops the removed.
  useEffect(() => {
    const subscribed = subscribedRef.current;
    if (readyState !== WebSocket.OPEN) {
      subscribed.clear();
      return;
    }
    const added = pairIds.filter((pairId) => !subscribed.has(pairId));
    const removed = [...subscribed].filter((pairId) => !pairIds.includes(pairId));
    if (added.length > 0) send(JSON.stringify({ act: 'subscribe', ps: added }));
    if (removed.length > 0) send(JSON.stringify({ act: 'unsubscribe', ps: removed }));
    for (const pairId of added) subscribed.add(pairId);
    for (const pairId of removed) subscribed.delete(pairId);
  }, [pairIds, readyState, send]);

  useEffect(
    () => () => {
      if (flushTimerRef.current !== null) clearTimeout(flushTimerRef.current);
    },
    [],
  );

  return quotes;
}
